// src/pages/Operations/CustomerChat.js
//
// Agent console for the residential customer support chat.
//
// Backend: /api/customer-chat (sowash-backend/routes/customerChatAdminRoutes.js).
// Access is Operations + Admin — enforced server-side by requireSupportAgent,
// not by hiding the menu item.
//
// Model: ONE thread per customer, with per-message job tagging. The customer
// app has no inbox; this side does, because an agent handles many customers.
//
// Transport is polling — the backend has no websocket layer. Two cadences:
// the inbox every 15s, the open conversation every 8s (delta only, via
// after_id), and both pause when the browser tab is hidden.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { getAuthHeader } from '../../helpers/auth';
import { fmtPKTDateTime, fmtPKTTime } from '../../helpers/time';

const API = process.env.REACT_APP_API_BASE_URL;
// Attachments come back as "/uploads/..." — SERVER_BASE must not repeat /api.
const IMG_BASE = API ? API.replace('/api', '') : '';

const T = {
  bg: 'var(--nd-bg)',
  surface: 'var(--nd-surface)',
  border: 'var(--nd-w06)',
  text: 'var(--nd-text)',
  textStrong: 'var(--nd-text-strong)',
  textMuted: 'var(--nd-tx35)',
  blue: '#3B82F6',
  blueLight: '#60A5FA',
  green: '#22D3A5',
  amber: '#F59E0B',
  red: '#F87171',
};

const GLOBAL = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700;9..40,800&family=DM+Mono:wght@400;500&display=swap');
  .nv-chat * { box-sizing: border-box; }
  .nv-chat ::-webkit-scrollbar { width: 7px; height: 7px; }
  .nv-chat ::-webkit-scrollbar-thumb { background: var(--nd-w08); border-radius: 4px; }
  .nv-chat ::-webkit-scrollbar-track { background: transparent; }

  .nv-chat input, .nv-chat select, .nv-chat textarea {
    background: var(--nd-w05); border: 1px solid var(--nd-w10); border-radius: 8px;
    color: var(--nd-text); font-family: 'DM Sans', sans-serif; font-size: 13px;
    padding: 8px 12px; outline: none;
  }
  .nv-chat input:focus, .nv-chat select:focus, .nv-chat textarea:focus { border-color: rgba(96,165,250,0.5); }

  .nv-chat .nv-thread { cursor: pointer; transition: background .12s; }
  .nv-chat .nv-thread:hover { background: rgba(96,165,250,0.06); }
  .nv-chat .nv-thread.nv-on { background: rgba(96,165,250,0.12); }

  .nv-chat .nv-tab {
    padding: 8px 14px; background: none; border: none; border-radius: 999px;
    color: var(--nd-tx35); font-family: 'DM Sans', sans-serif; font-weight: 700;
    font-size: 11.5px; cursor: pointer; white-space: nowrap;
  }
  .nv-chat .nv-tab.nv-on { color: var(--nd-text-strong); background: var(--nd-w08); }
`;

const STATUS_TABS = [
  { key: 'open', label: 'Open' },
  { key: 'closed', label: 'Closed' },
  { key: 'all', label: 'All' },
];

const INBOX_POLL_MS = 15000;
const THREAD_POLL_MS = 8000;

const initials = (name) => (name || '?')
  .split(' ')
  .filter(Boolean)
  .slice(0, 2)
  .map((p) => p[0].toUpperCase())
  .join('');

// "2m", "4h", "3d" — an agent scans the inbox for recency, not exact clock time.
const shortAgo = (iso) => {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(diff)) return '';
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  return `${Math.floor(hrs / 24)}d`;
};

/**
 * One labelled row in the job drawer.
 *
 * Renders nothing when the value is empty rather than showing "—" fifteen
 * times: a job that was never dispatched has no dispatch time, and a column of
 * dashes buries the three fields the agent actually needs.
 */
const JobField = ({ label, value, mono }) => {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div style={{ marginTop: 13 }}>
      <div style={{ fontSize: 9.5, fontWeight: 800, color: T.textMuted, letterSpacing: '0.08em', marginBottom: 3 }}>
        {label}
      </div>
      <div style={{ fontSize: 12.5, color: T.text, fontFamily: mono ? "'DM Mono', monospace" : undefined, wordBreak: 'break-word' }}>
        {value}
      </div>
    </div>
  );
};

const CustomerChat = () => {
  const [threads, setThreads] = useState([]);
  const [status, setStatus] = useState('open');
  const [search, setSearch] = useState('');
  const [activeId, setActiveId] = useState(null);
  const [thread, setThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [agents, setAgents] = useState([]);

  const [loadingInbox, setLoadingInbox] = useState(true);
  const [loadingThread, setLoadingThread] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  const [draft, setDraft] = useState('');
  const [photo, setPhoto] = useState(null);
  const [lightbox, setLightbox] = useState(null);

  // The tagged job, opened in place. A drawer rather than a link out: there is
  // no staff route for a single residential job order (/operations/list-job-orders
  // is a list), and navigating away would drop the 8s poll that keeps this
  // conversation live and lose the agent's half-typed reply.
  const [jobDrawer, setJobDrawer] = useState(null);
  const [jobLoading, setJobLoading] = useState(false);
  const [jobError, setJobError] = useState(null);

  const bodyRef = useRef(null);
  const fileRef = useRef(null);
  // Poll cursor. Held in a ref so the interval closure always sees the latest
  // value without being torn down and rebuilt on every new message.
  const lastIdRef = useRef(0);
  const activeIdRef = useRef(null);
  activeIdRef.current = activeId;

  const scrollToEnd = useCallback(() => {
    const el = bodyRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, []);

  /** Open the drawer for a job tagged onto a message. */
  const openJob = useCallback(async (jobOrderId) => {
    if (!jobOrderId) return;
    setJobDrawer(null);
    setJobError(null);
    setJobLoading(true);
    try {
      const res = await axios.get(`${API}/customer-chat/job/${jobOrderId}`, getAuthHeader());
      setJobDrawer(res.data?.job || null);
    } catch (err) {
      setJobError(err?.response?.data?.error || 'Could not load this job.');
    } finally {
      setJobLoading(false);
    }
  }, []);

  // ─────────────────────────── data ───────────────────────────

  const loadInbox = useCallback(async (quiet) => {
    if (!quiet) setLoadingInbox(true);
    try {
      const params = new URLSearchParams();
      if (status !== 'all') params.append('status', status);
      if (search.trim()) params.append('q', search.trim());
      const qs = params.toString() ? `?${params.toString()}` : '';

      const res = await axios.get(`${API}/customer-chat/threads${qs}`, getAuthHeader());
      setThreads(res.data?.threads || []);
      setError(null);
    } catch (err) {
      // A failed background refresh must not wipe the inbox the agent is
      // reading; only a foreground load surfaces the error.
      if (!quiet) setError(err?.response?.data?.error || 'Could not load conversations');
    } finally {
      if (!quiet) setLoadingInbox(false);
    }
  }, [status, search]);

  const openThread = useCallback(async (id) => {
    setActiveId(id);
    setLoadingThread(true);
    setDraft('');
    setPhoto(null);
    try {
      const res = await axios.get(`${API}/customer-chat/threads/${id}`, getAuthHeader());
      setThread(res.data?.thread || null);
      const list = res.data?.messages || [];
      setMessages(list);
      lastIdRef.current = list.reduce((m, x) => Math.max(m, x.id), 0);

      await axios.post(`${API}/customer-chat/threads/${id}/read`, {}, getAuthHeader());
      // Clear this row's badge immediately rather than waiting for the next
      // inbox poll — the agent is looking at the message right now.
      setThreads((prev) => prev.map((t) => (t.id === id ? { ...t, unread: 0 } : t)));
      setError(null);
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not open this conversation');
    } finally {
      setLoadingThread(false);
      setTimeout(scrollToEnd, 40);
    }
  }, [scrollToEnd]);

  const pollThread = useCallback(async () => {
    const id = activeIdRef.current;
    if (!id) return;
    try {
      const res = await axios.get(
        `${API}/customer-chat/threads/${id}/messages?after_id=${lastIdRef.current}`,
        getAuthHeader(),
      );
      const fresh = res.data?.messages || [];
      if (!fresh.length || activeIdRef.current !== id) return;

      setMessages((prev) => {
        const known = new Set(prev.map((m) => m.id));
        const add = fresh.filter((m) => !known.has(m.id));
        return add.length ? [...prev, ...add] : prev;
      });
      lastIdRef.current = fresh.reduce((m, x) => Math.max(m, x.id), lastIdRef.current);

      // Reading it as it arrives — keep the server's read mark in step.
      if (fresh.some((m) => m.sender_kind === 'customer')) {
        axios.post(`${API}/customer-chat/threads/${id}/read`, {}, getAuthHeader()).catch(() => {});
      }
      setTimeout(scrollToEnd, 40);
    } catch {
      // Silent — the next tick retries.
    }
  }, [scrollToEnd]);

  useEffect(() => { loadInbox(false); }, [loadInbox]);

  useEffect(() => {
    axios.get(`${API}/customer-chat/agents`, getAuthHeader())
      .then((res) => setAgents(res.data?.agents || []))
      .catch(() => { /* assignment dropdown just stays empty */ });
  }, []);

  // Both pollers stop while the tab is hidden — an agent with this parked in a
  // background tab all day would otherwise hammer the API for nothing.
  useEffect(() => {
    const tick = () => {
      if (document.hidden) return;
      loadInbox(true);
    };
    const timer = setInterval(tick, INBOX_POLL_MS);
    return () => clearInterval(timer);
  }, [loadInbox]);

  useEffect(() => {
    const tick = () => {
      if (document.hidden) return;
      pollThread();
    };
    const timer = setInterval(tick, THREAD_POLL_MS);
    return () => clearInterval(timer);
  }, [pollThread]);

  // ─────────────────────────── actions ───────────────────────────

  const sendReply = async () => {
    const body = draft.trim();
    if ((!body && !photo) || !activeId || sending) return;

    setSending(true);
    try {
      let res;
      if (photo) {
        const form = new FormData();
        if (body) form.append('body', body);
        form.append('photo', photo);
        res = await axios.post(`${API}/customer-chat/threads/${activeId}/messages`, form, {
          headers: { ...getAuthHeader().headers, 'Content-Type': 'multipart/form-data' },
        });
      } else {
        res = await axios.post(
          `${API}/customer-chat/threads/${activeId}/messages`,
          { body },
          getAuthHeader(),
        );
      }

      const saved = res.data?.message;
      if (saved) {
        setMessages((prev) => [...prev, saved]);
        lastIdRef.current = Math.max(lastIdRef.current, saved.id);
      }
      setDraft('');
      setPhoto(null);
      if (fileRef.current) fileRef.current.value = '';
      setError(null);
      loadInbox(true);
      setTimeout(scrollToEnd, 40);
    } catch (err) {
      setError(err?.response?.data?.error || 'Your reply could not be sent');
    } finally {
      setSending(false);
    }
  };

  const patchThread = async (payload) => {
    if (!activeId) return;
    try {
      const res = await axios.patch(`${API}/customer-chat/threads/${activeId}`, payload, getAuthHeader());
      setThread((prev) => ({ ...prev, ...(res.data?.thread || {}) }));
      loadInbox(true);
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not update this conversation');
    }
  };

  // Enter sends, Shift+Enter breaks the line — the convention every agent
  // already has in their fingers from WhatsApp and Slack.
  const onComposerKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendReply();
    }
  };

  const totalUnread = useMemo(
    () => threads.reduce((n, t) => n + (Number(t.unread) || 0), 0),
    [threads],
  );

  const card = { background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, overflow: 'hidden' };

  // ─────────────────────────── render ───────────────────────────

  return (
    <div className="nv-chat" style={{ background: T.bg, fontFamily: "'DM Sans', sans-serif", color: T.text, padding: '14px 18px', minHeight: '100vh' }}>
      <style>{GLOBAL}</style>

      <div style={{ ...card, marginBottom: 12 }}>
        <div style={{ padding: '20px 24px 18px' }}>
          <div style={{ fontSize: 9, color: T.textMuted, letterSpacing: '0.18em', marginBottom: 6 }}>NOVAA · CUSTOMER SUPPORT</div>
          <div style={{ fontSize: 27, fontWeight: 800, color: T.textStrong, letterSpacing: '-0.03em', lineHeight: 1 }}>Messages</div>
          <div style={{ fontSize: 12.5, color: T.textMuted, marginTop: 8 }}>
            <span style={{ color: T.text, fontWeight: 600 }}>{threads.length}</span> conversation{threads.length === 1 ? '' : 's'}
            {totalUnread > 0 && (
              <>
                {' · '}
                <span style={{ color: T.red, fontWeight: 700 }}>{totalUnread}</span> unread
              </>
            )}
          </div>
        </div>
        <div style={{ height: 2, background: 'linear-gradient(90deg,#3B82F6,#22D3A5,transparent)' }} />
      </div>

      {error && (
        <div style={{ ...card, padding: '12px 18px', marginBottom: 12, color: T.red, fontSize: 12.5 }}>⚠ {error}</div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '340px 1fr', gap: 12, height: 'calc(100vh - 190px)', minHeight: 480 }}>

        {/* ─── Inbox ─── */}
        <div style={{ ...card, display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '12px 14px', borderBottom: `1px solid ${T.border}` }}>
            <input
              placeholder="Search name, phone or ID…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ width: '100%', marginBottom: 10 }}
            />
            <div style={{ display: 'flex', gap: 4 }}>
              {STATUS_TABS.map((t) => (
                <button
                  key={t.key}
                  className={`nv-tab${status === t.key ? ' nv-on' : ''}`}
                  onClick={() => setStatus(t.key)}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          <div style={{ flex: 1, overflowY: 'auto' }}>
            {loadingInbox ? (
              <div style={{ padding: 32, textAlign: 'center', color: T.textMuted, fontSize: 12.5 }}>Loading…</div>
            ) : threads.length === 0 ? (
              <div style={{ padding: 32, textAlign: 'center', color: T.textMuted, fontSize: 12.5 }}>
                No {status === 'all' ? '' : status} conversations
              </div>
            ) : threads.map((t) => {
              const unread = Number(t.unread) || 0;
              return (
                <div
                  key={t.id}
                  className={`nv-thread${activeId === t.id ? ' nv-on' : ''}`}
                  onClick={() => openThread(t.id)}
                  style={{ display: 'flex', gap: 11, padding: '12px 14px', borderBottom: `1px solid var(--nd-w04)` }}
                >
                  <div style={{
                    width: 36, height: 36, borderRadius: 12, flexShrink: 0,
                    background: unread ? 'rgba(248,113,113,0.15)' : 'var(--nd-w06)',
                    color: unread ? T.red : T.textMuted,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 12, fontWeight: 800,
                  }}>
                    {initials(t.client_name)}
                  </div>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                      <span style={{
                        fontSize: 13, fontWeight: unread ? 800 : 600,
                        color: unread ? T.textStrong : T.text,
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                      }}>
                        {t.client_name || t.cust_id}
                      </span>
                      <span style={{ marginLeft: 'auto', fontSize: 10, color: T.textMuted, fontFamily: "'DM Mono', monospace", flexShrink: 0 }}>
                        {shortAgo(t.last_message_at)}
                      </span>
                    </div>
                    <div style={{
                      fontSize: 11.5, color: unread ? T.text : T.textMuted, marginTop: 3,
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}>
                      {t.last_sender_kind === 'agent' && <span style={{ color: T.textMuted }}>You: </span>}
                      {t.last_message_preview || '—'}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 5 }}>
                      {t.client_city && <span style={{ fontSize: 9, color: 'var(--nd-w20)' }}>{t.client_city}</span>}
                      {t.assigned_to && (
                        <span style={{ fontSize: 9, color: T.blueLight, background: 'rgba(96,165,250,0.12)', padding: '1px 6px', borderRadius: 5 }}>
                          {t.assigned_to}
                        </span>
                      )}
                      {t.status === 'closed' && (
                        <span style={{ fontSize: 9, color: T.textMuted, background: 'var(--nd-w06)', padding: '1px 6px', borderRadius: 5 }}>closed</span>
                      )}
                    </div>
                  </div>

                  {unread > 0 && (
                    <div style={{
                      alignSelf: 'center', minWidth: 19, height: 19, borderRadius: 10,
                      background: T.red, color: '#fff', fontSize: 10, fontWeight: 800,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 6px',
                    }}>
                      {unread}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* ─── Conversation ─── */}
        <div style={{ ...card, display: 'flex', flexDirection: 'column' }}>
          {!activeId ? (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: T.textMuted, fontSize: 13 }}>
              Select a conversation to reply
            </div>
          ) : (
            <>
              <div style={{ padding: '13px 18px', borderBottom: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 15, fontWeight: 800, color: T.textStrong }}>
                    {thread?.client_name || thread?.cust_id || '—'}
                  </div>
                  <div style={{ fontSize: 11, color: T.textMuted, marginTop: 2 }}>
                    {thread?.phone_number || 'no phone'}
                    {thread?.client_city ? ` · ${thread.client_city}` : ''}
                    {thread?.cust_id ? ` · ${thread.cust_id}` : ''}
                  </div>
                </div>

                <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <select
                    value={thread?.assigned_user_id ?? ''}
                    onChange={(e) => patchThread({ assigned_user_id: e.target.value === '' ? null : Number(e.target.value) })}
                    style={{ fontSize: 11.5 }}
                    title="Assign this conversation"
                  >
                    <option value="">Unassigned</option>
                    {agents.map((a) => <option key={a.id} value={a.id}>{a.name || `User ${a.id}`}</option>)}
                  </select>

                  <button
                    onClick={() => patchThread({ status: thread?.status === 'closed' ? 'open' : 'closed' })}
                    style={{
                      padding: '7px 13px', borderRadius: 8, cursor: 'pointer', fontSize: 11.5, fontWeight: 700,
                      background: 'var(--nd-w05)', border: `1px solid ${T.border}`,
                      color: thread?.status === 'closed' ? T.green : T.textMuted,
                    }}
                  >
                    {thread?.status === 'closed' ? '↻ Reopen' : '✓ Close'}
                  </button>
                </div>
              </div>

              <div ref={bodyRef} style={{ flex: 1, overflowY: 'auto', padding: '18px 20px' }}>
                {loadingThread ? (
                  <div style={{ textAlign: 'center', color: T.textMuted, fontSize: 12.5, padding: 30 }}>Loading…</div>
                ) : messages.length === 0 ? (
                  <div style={{ textAlign: 'center', color: T.textMuted, fontSize: 12.5, padding: 30 }}>No messages yet</div>
                ) : messages.map((m) => {
                  const mine = m.sender_kind === 'agent';
                  if (m.sender_kind === 'system') {
                    return (
                      <div key={m.id} style={{ textAlign: 'center', margin: '12px 0' }}>
                        <span style={{ fontSize: 10.5, color: T.textMuted, background: 'var(--nd-w05)', padding: '4px 11px', borderRadius: 9 }}>{m.body}</span>
                      </div>
                    );
                  }
                  return (
                    <div key={m.id} style={{ display: 'flex', justifyContent: mine ? 'flex-end' : 'flex-start', marginBottom: 12 }}>
                      <div style={{
                        maxWidth: '68%', padding: '10px 13px', borderRadius: 14,
                        background: mine ? 'rgba(59,130,246,0.16)' : 'var(--nd-w05)',
                        border: `1px solid ${mine ? 'rgba(59,130,246,0.30)' : 'var(--nd-w08)'}`,
                        borderBottomRightRadius: mine ? 5 : 14,
                        borderBottomLeftRadius: mine ? 14 : 5,
                      }}>
                        {mine && m.agent_name && (
                          <div style={{ fontSize: 10, fontWeight: 800, color: T.blueLight, marginBottom: 4 }}>{m.agent_name}</div>
                        )}

                        {m.job && (
                          // Clickable: the customer tagged this job so the agent
                          // could see which visit they mean — reading the label
                          // and then hunting for it in the job list is the part
                          // that made tagging useless.
                          <button
                            type="button"
                            onClick={() => openJob(m.job.id)}
                            title="Open this job"
                            style={{
                              display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 7,
                              fontSize: 10, fontWeight: 700, color: T.amber,
                              background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.28)',
                              padding: '3px 8px', borderRadius: 6, cursor: 'pointer',
                              fontFamily: "'DM Sans', sans-serif",
                            }}
                          >
                            ⌗ {m.job.service_type || 'Service'}
                            {m.job.scheduled_date ? ` · ${m.job.scheduled_date}` : ''}
                            {m.job.status ? ` · ${m.job.status}` : ''}
                            <span style={{ opacity: 0.7 }}>›</span>
                          </button>
                        )}

                        {m.attachment_url && (
                          <img
                            src={`${IMG_BASE}${m.attachment_url}`}
                            alt={m.attachment_name || 'attachment'}
                            loading="lazy"
                            onClick={() => setLightbox(`${IMG_BASE}${m.attachment_url}`)}
                            style={{ display: 'block', maxWidth: 260, maxHeight: 220, borderRadius: 9, cursor: 'pointer', marginBottom: m.body ? 8 : 4, border: '1px solid var(--nd-w10)' }}
                          />
                        )}

                        {m.body && (
                          <div style={{ fontSize: 13, color: T.text, lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{m.body}</div>
                        )}

                        <div style={{ fontSize: 9.5, color: T.textMuted, marginTop: 6, fontFamily: "'DM Mono', monospace" }} title={fmtPKTDateTime(m.created_at)}>
                          {fmtPKTTime(m.created_at)}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div style={{ borderTop: `1px solid ${T.border}`, padding: '12px 16px' }}>
                {photo && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 9, fontSize: 11.5, color: T.textMuted }}>
                    <img src={URL.createObjectURL(photo)} alt="" style={{ width: 30, height: 30, borderRadius: 6, objectFit: 'cover' }} />
                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{photo.name}</span>
                    <button
                      onClick={() => { setPhoto(null); if (fileRef.current) fileRef.current.value = ''; }}
                      style={{ background: 'none', border: 'none', color: T.textMuted, cursor: 'pointer', fontSize: 14 }}
                    >✕</button>
                  </div>
                )}

                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 9 }}>
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/*"
                    onChange={(e) => setPhoto(e.target.files?.[0] || null)}
                    style={{ display: 'none' }}
                  />
                  <button
                    onClick={() => fileRef.current?.click()}
                    title="Attach a photo"
                    style={{ padding: '9px 12px', background: 'var(--nd-w05)', border: `1px solid ${T.border}`, borderRadius: 8, color: T.textMuted, cursor: 'pointer', fontSize: 14 }}
                  >📎</button>

                  <textarea
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={onComposerKey}
                    placeholder="Write a reply…  (Enter to send, Shift+Enter for a new line)"
                    rows={2}
                    style={{ flex: 1, resize: 'none', fontFamily: "'DM Sans', sans-serif", lineHeight: 1.5 }}
                  />

                  <button
                    onClick={sendReply}
                    disabled={sending || (!draft.trim() && !photo)}
                    style={{
                      padding: '10px 20px', borderRadius: 8, border: 'none',
                      background: (sending || (!draft.trim() && !photo)) ? 'var(--nd-w08)' : 'linear-gradient(135deg,#3B82F6,#22D3A5)',
                      color: (sending || (!draft.trim() && !photo)) ? T.textMuted : '#fff',
                      fontWeight: 800, fontSize: 12.5,
                      cursor: (sending || (!draft.trim() && !photo)) ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {sending ? 'Sending…' : 'Send'}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {(jobDrawer || jobLoading || jobError) && (
        <div
          onClick={() => { setJobDrawer(null); setJobError(null); }}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(3px)', zIndex: 9999, display: 'flex', justifyContent: 'flex-end' }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: 380, maxWidth: '92vw', height: '100%', background: T.surface,
              borderLeft: `1px solid ${T.border}`, padding: '18px 20px', overflowY: 'auto',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: T.textStrong, letterSpacing: '0.04em' }}>
                JOB DETAILS
              </div>
              <button
                type="button"
                onClick={() => { setJobDrawer(null); setJobError(null); }}
                style={{ background: 'none', border: 'none', color: T.textMuted, fontSize: 18, cursor: 'pointer', lineHeight: 1 }}
              >
                ×
              </button>
            </div>

            {jobLoading && (
              <div style={{ color: T.textMuted, fontSize: 12.5, padding: '20px 0' }}>Loading…</div>
            )}

            {jobError && !jobLoading && (
              <div style={{ color: T.red, fontSize: 12.5, padding: '20px 0' }}>{jobError}</div>
            )}

            {jobDrawer && !jobLoading && (
              <>
                <div style={{ fontSize: 16, fontWeight: 800, color: T.textStrong }}>
                  {jobDrawer.service_type || 'Panel cleaning'}
                </div>
                <div style={{ fontSize: 11.5, color: T.textMuted, marginTop: 3, fontFamily: "'DM Mono', monospace" }}>
                  Job #{jobDrawer.id} · {jobDrawer.status || '—'}
                </div>

                <JobField label="CUSTOMER" value={jobDrawer.client_name} />
                <JobField label="CUSTOMER ID" value={jobDrawer.cust_id} mono />
                <JobField label="ADDRESS" value={jobDrawer.address} />

                {/* scheduled_date and actual_cleaning_date are DATE columns —
                    rendered raw on purpose. Running a date-only value through a
                    timezone formatter is how it slides to the previous day. */}
                <JobField label="SCHEDULED" value={jobDrawer.scheduled_date} mono />
                <JobField label="CLEANED ON" value={jobDrawer.actual_cleaning_date} mono />
                <JobField label="TEAM" value={jobDrawer.assigned_team} />
                <JobField label="PLAN" value={jobDrawer.frequency} />
                <JobField label="SYSTEM SIZE" value={jobDrawer.system_size} />

                {/* These four ARE instants, so they do go through the PKT formatter. */}
                <JobField label="DISPATCHED" value={jobDrawer.dispatched_at && fmtPKTDateTime(jobDrawer.dispatched_at)} mono />
                <JobField label="STARTED" value={jobDrawer.started_at && fmtPKTDateTime(jobDrawer.started_at)} mono />
                <JobField label="COMPLETED" value={jobDrawer.completed_at && fmtPKTDateTime(jobDrawer.completed_at)} mono />
                <JobField label="CANCELLED" value={jobDrawer.cancelled_at && fmtPKTDateTime(jobDrawer.cancelled_at)} mono />

                <JobField
                  label="PAYMENT"
                  value={[
                    jobDrawer.is_paid ? 'Paid' : 'Unpaid',
                    jobDrawer.payment_method,
                    jobDrawer.cash_collected ? 'cash collected' : null,
                  ].filter(Boolean).join(' · ')}
                />

                {/* Counts, not thumbnails: job_orders image columns hold a mix of
                    bare filenames and full VPS paths, and the normalisation that
                    sorts that out lives in customerAppRoutes.js. Rendering them
                    here would ship broken <img> tags for the older rows. */}
                <JobField
                  label="PHOTOS"
                  value={`${(jobDrawer.before_images || []).length} before · ${(jobDrawer.after_images || []).length} after`}
                />

                {jobDrawer.notes ? (
                  <div style={{ marginTop: 16 }}>
                    <div style={{ fontSize: 9.5, fontWeight: 800, color: T.textMuted, letterSpacing: '0.08em', marginBottom: 5 }}>NOTES</div>
                    <div style={{ fontSize: 12.5, color: T.text, lineHeight: 1.5, whiteSpace: 'pre-wrap', background: 'var(--nd-w05)', border: `1px solid ${T.border}`, borderRadius: 8, padding: '9px 11px' }}>
                      {jobDrawer.notes}
                    </div>
                  </div>
                ) : null}
              </>
            )}
          </div>
        </div>
      )}

      {lightbox && (
        <div
          onClick={() => setLightbox(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000, padding: 24 }}
        >
          <img src={lightbox} alt="Attachment" style={{ maxWidth: '90vw', maxHeight: '88vh', borderRadius: 12, border: '1px solid var(--nd-w15)' }} />
        </div>
      )}
    </div>
  );
};

export default CustomerChat;
